import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: 'pay-per-scan' | 'pro';
}
export function CancelSubscriptionModal({
  isOpen,
  onClose,
  currentPlan
}: CancelSubscriptionModalProps) {
  const {
    user,
    updateUserProfile
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const handleCancelSubscription = async () => {
    if (!user?.id) {
      setError('You must be logged in to cancel subscription');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Call backend to cancel subscription
      const response = await fetch('https://project-observant-artichoke-789.magicpatterns.app/functions/v1/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: user.id
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }
      // Update user profile to free plan
      await updateUserProfile({
        subscriptionPlan: 'free'
      });
      // Close modal and show success
      onClose();
      window.location.reload(); // Refresh to show updated plan
    } catch (err: any) {
      console.error('Cancellation error:', err);
      setError(err.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1750">
      <div className="bg-white rounded-2xl max-w-md w-full p-6" data-id="element-1751">
        <div className="flex items-center justify-between mb-4" data-id="element-1752">
          <div className="flex items-center" data-id="element-1753">
            <div className="bg-red-100 p-2 rounded-lg mr-3" data-id="element-1754">
              <AlertTriangle size={24} className="text-red-600" data-id="element-1755" />
            </div>
            <h2 className="text-xl font-bold" data-id="element-1756">Cancel Subscription</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1757">
            <X size={24} data-id="element-1758" />
          </button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4" data-id="element-1759">
            <p className="text-red-800 text-sm" data-id="element-1760">{error}</p>
          </div>}

        <div className="mb-6" data-id="element-1761">
          <p className="text-gray-700 mb-4" data-id="element-1762">
            Are you sure you want to cancel your{' '}
            <strong data-id="element-1763">{currentPlan === 'pro' ? 'Pro' : 'Pay per Scan'}</strong>{' '}
            subscription?
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4" data-id="element-1764">
            <h3 className="font-semibold text-yellow-900 mb-2" data-id="element-1765">
              You will lose access to:
            </h3>
            <ul className="space-y-2 text-sm text-yellow-800" data-id="element-1766">
              {currentPlan === 'pro' ? <>
                  <li data-id="element-1767">• Unlimited scans</li>
                  <li data-id="element-1768">• Advanced pattern detection</li>
                  <li data-id="element-1769">• Detailed PDF reports</li>
                  <li data-id="element-1770">• Priority support</li>
                </> : <>
                  <li data-id="element-1771">• Paid scan credits</li>
                  <li data-id="element-1772">• Full AI insights & analysis</li>
                  <li data-id="element-1773">• Detailed pattern labels</li>
                  <li data-id="element-1774">• Personalized advice</li>
                </>}
            </ul>
          </div>

          <div className="flex items-start mb-4" data-id="element-1775">
            <input type="checkbox" id="confirm-cancel" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded" data-id="element-1776" />
            <label htmlFor="confirm-cancel" className="text-sm text-gray-700" data-id="element-1777">
              I understand that I will lose access to premium features and my
              subscription will be cancelled immediately.
            </label>
          </div>
        </div>

        <div className="flex gap-3" data-id="element-1778">
          <button onClick={onClose} disabled={loading} className="flex-1 bg-gray-100 text-gray-700 rounded-lg py-3 font-medium hover:bg-gray-200 transition-colors disabled:opacity-50" data-id="element-1779">
            Keep Subscription
          </button>
          <button onClick={handleCancelSubscription} disabled={!confirmed || loading} className="flex-1 bg-red-600 text-white rounded-lg py-3 font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1780">
            {loading ? 'Cancelling...' : 'Cancel Subscription'}
          </button>
        </div>
      </div>
    </div>;
}